import { useCallback, useEffect, useRef, useState } from "react";
import type { User } from "@lhe/shared";
import {
  ApiRequestError,
  api,
  onUnauthenticated,
  type AuthConfig,
} from "../lib/api";
import { isElectronRuntime } from "../lib/runtime";

export type SessionState =
  | { status: "loading" }
  | { status: "authenticated"; user: User }
  | { status: "anonymous" };

export interface Session {
  state: SessionState;
  /** 로그인 화면이 어떤 버튼을 그릴지. 로딩 중이거나 조회 실패 시 undefined. */
  config: AuthConfig | undefined;
  /**
   * 설정 조회 자체가 실패한 이유. API 서버가 안 떠 있는 경우가 대부분이다.
   *
   * "로그인 수단이 없다"와 "서버에 못 붙었다"는 완전히 다른 문제인데, 예전에는
   * 둘 다 똑같이 "로그인 수단 없음"으로 보여서 원인을 찾기 어려웠다.
   */
  configError: string | undefined;
  /**
   * 작업 중에 세션이 끊겼다. 안내 모달을 띄우는 조건이다.
   *
   * 상태를 곧바로 `anonymous` 로 바꾸지 않는 이유: 그러면 라우터가 즉시 로그인
   * 화면으로 보내 버려서, 사용자는 편집하던 화면이 왜 사라졌는지 모른 채
   * 로그인 화면을 마주하게 된다.
   */
  expired: boolean;
  /** 로그인 성공 후 세션을 다시 읽는다. */
  refresh: () => Promise<void>;
  logout: () => Promise<void>;
  /** 만료 안내에서 [로그인 페이지로 이동] 을 눌렀을 때. 라우터 가드가 보낸다. */
  goToLogin: () => void;
  /** 만료 안내만 닫는다. 편집하던 내용을 복사해 두거나 다른 탭에서 다시 로그인할 때. */
  dismissExpiry: () => void;
}

/**
 * 세션 상태. 인증 여부는 서버가 쿠키로 판단하므로 클라이언트는 `/auth/me` 결과만 믿는다.
 * localStorage 에 로그인 상태를 캐시하지 말 것 — 서버 세션과 어긋난다.
 */
export function useSession(): Session {
  const [state, setState] = useState<SessionState>({ status: "loading" });
  const [config, setConfig] = useState<AuthConfig>();
  const [configError, setConfigError] = useState<string>();
  const [expired, setExpired] = useState(false);

  /**
   * 만료 안내를 한 번 닫았다. 그 뒤로는 다시 열지 않는다.
   *
   * 닫는 이유는 대개 "편집하던 내용을 복사해 두거나 다른 탭에서 로그인하려고"
   * 다. 그런데 그 사이에도 자동 저장은 계속 돌고 그때마다 401 이 온다 —
   * 매번 다시 띄우면 하려던 일을 못 한다. 다시 로그인해 세션이 살아나면
   * (`refresh` 성공) 초기화된다.
   */
  const expiryDismissedRef = useRef(false);

  const refresh = useCallback(async () => {
    try {
      const user = isElectronRuntime()
        ? (await api.auth.renewDesktopSession()).user
        : await api.auth.me();
      setState({ status: "authenticated", user });
      expiryDismissedRef.current = false;
      setExpired(false);
    } catch (cause) {
      if (
        !(cause instanceof ApiRequestError && cause.code === "UNAUTHENTICATED")
      ) {
        console.error("[session] 확인 실패", cause);
      }
      setState({ status: "anonymous" });
    }
  }, []);

  const logout = useCallback(async () => {
    await api.auth
      .logout()
      .catch((cause) => console.error("[session] 로그아웃 실패", cause));
    await window.twPagesDesktop
      ?.clearLoginSession()
      .catch((cause) => console.error("[session] 로컬 쿠키 삭제 실패", cause));
    setExpired(false);
    setState({ status: "anonymous" });
  }, []);

  const goToLogin = useCallback(() => {
    setExpired(false);
    // 상태만 바꾸면 App 의 라우터 가드가 /login 으로 보낸다. 여기서 경로를 직접
    // 다루지 않는다 — 로그인 화면의 위치를 아는 곳은 라우터 한 곳이면 된다.
    setState({ status: "anonymous" });
  }, []);

  const dismissExpiry = useCallback(() => {
    expiryDismissedRef.current = true;
    setExpired(false);
  }, []);

  /*
   * 어떤 요청이든 401 을 받으면 알림이 온다(lib/api.ts).
   *
   * **로그인한 적이 있는 경우에만** 만료로 본다. 처음 들어온 사용자의 `/auth/me`
   * 도 401 이지만 그건 만료가 아니라 그냥 미로그인이고, 그때 만료 모달을 띄우면
   * 로그인 화면 위에 "로그인이 만료되었습니다" 가 뜬다.
   *
   * 최신 상태를 ref 로 읽는 이유: 구독은 한 번만 걸고(중복 구독을 피한다) 콜백은
   * 그때의 state 클로저에 묶이면 안 된다.
   */
  const stateRef = useRef(state);
  stateRef.current = state;

  useEffect(
    () =>
      onUnauthenticated(() => {
        if (stateRef.current.status !== "authenticated") return;
        if (expiryDismissedRef.current) return;
        setExpired(true);
      }),
    [],
  );

  useEffect(() => {
    void refresh();
    let isSubscribed = true;
    let retries = 3;

    const fetchConfig = () => {
      api.auth
        .config()
        .then((next) => {
          if (!isSubscribed) return;
          setConfig(next);
          setConfigError(undefined);
        })
        .catch((cause: Error) => {
          if (!isSubscribed) return;
          if (retries > 0) {
            retries--;
            setTimeout(fetchConfig, 1000);
            return;
          }
          setConfig(undefined);
          setConfigError(cause.message);
        });
    };

    fetchConfig();

    return () => {
      isSubscribed = false;
    };
  }, [refresh]);

  return {
    state,
    config,
    configError,
    expired,
    refresh,
    logout,
    goToLogin,
    dismissExpiry,
  };
}
